import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import * as participantesService from '@/services/participantes.service'
import type { Database } from '@/types/database'

type EventoParticipanteInsert = Database['public']['Tables']['evento_participantes']['Insert']

// Hook para listar participantes
export function useParticipantes(eventoId: string | undefined) {
  return useQuery({
    queryKey: ['participantes', eventoId],
    queryFn: () => participantesService.listarParticipantes(eventoId!),
    enabled: !!eventoId,
  })
}

// Hook para buscar participante
export function useParticipante(id: string | undefined) {
  return useQuery({
    queryKey: ['participante', id],
    queryFn: () => participantesService.buscarParticipante(id!),
    enabled: !!id,
  })
}

// Hook para estatísticas do evento
export function useEstatisticasEvento(eventoId: string | undefined) {
  return useQuery({
    queryKey: ['estatisticas-evento', eventoId],
    queryFn: () => participantesService.buscarEstatisticasEvento(eventoId!),
    enabled: !!eventoId,
  })
}

// Hook para criar participante
export function useCriarParticipante() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (participante: EventoParticipanteInsert) =>
      participantesService.criarParticipante(participante),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['participantes', data.evento_id] })
      queryClient.invalidateQueries({ queryKey: ['estatisticas-evento', data.evento_id] })
    },
  })
}

// Hook para atualizar participante
export function useAtualizarParticipante() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, dados }: { id: string; dados: Partial<EventoParticipanteInsert> }) =>
      participantesService.atualizarParticipante(id, dados),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['participantes', data.evento_id] })
      queryClient.invalidateQueries({ queryKey: ['participante', data.id] })
      queryClient.invalidateQueries({ queryKey: ['estatisticas-evento', data.evento_id] })
    },
  })
}

// Hook para deletar participante
export function useDeletarParticipante() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (variables: { id: string; eventoId: string }) =>
      participantesService.deletarParticipante(variables.id),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['participantes', variables.eventoId] })
      queryClient.invalidateQueries({ queryKey: ['estatisticas-evento', variables.eventoId] })
    },
  })
}

// Hook para atualizar status
export function useAtualizarStatusParticipante() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      id,
      status,
    }: {
      id: string
      status: 'confirmado' | 'pendente' | 'cancelado'
    }) => participantesService.atualizarStatusParticipante(id, status),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['participantes', data.evento_id] })
      queryClient.invalidateQueries({ queryKey: ['participante', data.id] })
      queryClient.invalidateQueries({ queryKey: ['estatisticas-evento', data.evento_id] })
    },
  })
}

// Hook para confirmar pagamento
export function useConfirmarPagamento() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (params: {
      participanteId: string
      eventoId: string
      metodo: 'pix' | 'dinheiro' | 'cartao'
      valor: number
      registradoPor: string
    }) =>
      participantesService.confirmarPagamento({
        participanteId: params.participanteId,
        metodo: params.metodo,
        valor: params.valor,
        registradoPor: params.registradoPor,
      }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['participantes', variables.eventoId] })
      queryClient.invalidateQueries({ queryKey: ['participante', variables.participanteId] })
      queryClient.invalidateQueries({ queryKey: ['estatisticas-evento', variables.eventoId] })
    },
  })
}

// Hook para marcar como isento
export function useMarcarComoIsento() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (variables: {
      participanteId: string
      eventoId: string
      registradoPor: string
    }) => participantesService.marcarComoIsento(variables.participanteId, variables.registradoPor),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['participantes', variables.eventoId] })
      queryClient.invalidateQueries({ queryKey: ['participante', variables.participanteId] })
      queryClient.invalidateQueries({ queryKey: ['estatisticas-evento', variables.eventoId] })
    },
  })
}

// Hook para registrar check-in
export function useRegistrarCheckin() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (variables: {
      participanteId: string
      eventoId: string
      registradoPor: string
    }) => participantesService.registrarCheckin(variables.participanteId, variables.registradoPor),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['participantes', variables.eventoId] })
      queryClient.invalidateQueries({ queryKey: ['participante', variables.participanteId] })
      queryClient.invalidateQueries({ queryKey: ['estatisticas-evento', variables.eventoId] })
    },
  })
}

// Hook para desfazer check-in
export function useDesfazerCheckin() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (variables: { participanteId: string; eventoId: string }) =>
      participantesService.desfazerCheckin(variables.participanteId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['participantes', variables.eventoId] })
      queryClient.invalidateQueries({ queryKey: ['participante', variables.participanteId] })
      queryClient.invalidateQueries({ queryKey: ['estatisticas-evento', variables.eventoId] })
    },
  })
}

// Hook para salvar respostas
export function useSalvarRespostas() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      participanteId,
      respostas,
    }: {
      participanteId: string
      respostas: Array<{
        pergunta_id: string
        resposta: string
      }>
    }) => participantesService.salvarRespostas(participanteId, respostas),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['participante', variables.participanteId] })
    },
  })
}

// Hook para upload de comprovante
export function useUploadComprovante() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      file,
      igrejaId,
      eventoId,
      participanteId,
    }: {
      file: File
      igrejaId: string
      eventoId: string
      participanteId: string
    }) =>
      participantesService.uploadComprovante(file, igrejaId, eventoId, participanteId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['participante', variables.participanteId] })
    },
  })
}
